/**
 * Genel Firmalar listesi AĞACI (#215) — saf yardımcılar.
 *
 * Sunucu her firmaya üst zincirini verir (`ancestors`: kökten doğrudan
 * ebeveyne; ebeveyn kuralı: ana firma → yoksa müşteride bayi → bayide üst
 * bayi). Ağaç bundan kurulur:
 *  - Listedeki (eşleşen) firmalar tam satır olur.
 *  - Zincirde olup listede olmayan üst firmalar (arama/filtre sonucu) "stub"
 *    düğüm olur — panel bunları soluk, sade satır olarak çizer.
 */

const byDealerThenName = (a, b) => {
  const ad = a.firm.firmType === "dealer" ? 0 : 1;
  const bd = b.firm.firmType === "dealer" ? 0 : 1;
  if (ad !== bd) return ad - bd;
  return String(a.firm.firmName || "").localeCompare(String(b.firm.firmName || ""), "tr");
};

/**
 * @param {Object[]} firms listedeki firmalar (her biri `ancestors` taşıyabilir)
 * @returns {Object[]} kök düğümler: { id, firm, matched, children, descendantCount }
 */
export function buildFirmTree(firms = []) {
  const nodes = new Map();
  const parentOf = new Map();

  const ensure = (firm, matched) => {
    const id = String(firm._id);
    const existing = nodes.get(id);
    if (existing) {
      // Aynı firma hem zincirde hem listede ise tam veri kazanır.
      if (matched && !existing.matched) {
        existing.firm = firm;
        existing.matched = true;
      }
      return existing;
    }
    const node = { id, firm, matched, children: [], descendantCount: 0 };
    nodes.set(id, node);
    return node;
  };

  (firms || []).forEach((firm) => {
    if (!firm?._id) return;
    ensure(firm, true);
  });

  (firms || []).forEach((firm) => {
    if (!firm?._id) return;
    const chain = Array.isArray(firm.ancestors) ? firm.ancestors : [];
    let parentId = null;
    chain.forEach((anc) => {
      if (!anc?._id) return;
      const ancNode = ensure(anc, false);
      if (parentId && !parentOf.has(ancNode.id) && parentId !== ancNode.id) {
        parentOf.set(ancNode.id, parentId);
      }
      parentId = ancNode.id;
    });
    const id = String(firm._id);
    if (parentId && parentId !== id) parentOf.set(id, parentId);
  });

  parentOf.forEach((parentId, childId) => {
    const parent = nodes.get(parentId);
    const child = nodes.get(childId);
    if (parent && child) parent.children.push(child);
  });

  const countDesc = (node, seen) => {
    if (seen.has(node.id)) return 0;
    seen.add(node.id);
    node.children.sort(byDealerThenName);
    node.descendantCount = node.children.reduce(
      (sum, c) => sum + 1 + countDesc(c, seen),
      0
    );
    return node.descendantCount;
  };

  const roots = [...nodes.values()].filter((n) => !parentOf.has(n.id));
  roots.sort(byDealerThenName);
  const seen = new Set();
  roots.forEach((r) => countDesc(r, seen));
  return roots;
}

/**
 * Ağacı ekranda görünen satırlara açar.
 * @param {Object[]} roots buildFirmTree çıktısı (ya da onun bir sayfası)
 * @param {(id: string) => boolean} isExpanded
 * @returns {Object[]} { node, depth, hasChildren, expanded, root, groupOpen }
 *   root      — satırın bağlı olduğu kök düğüm (kendisi kökse kendisi)
 *   groupOpen — kök açık mı: açık grubun tüm satırları aynı renk tonunu alır (#215)
 */
export function flattenFirmTree(roots = [], isExpanded = () => false) {
  const rows = [];
  const seen = new Set();
  const walk = (node, depth, root, groupOpen) => {
    if (seen.has(node.id)) return;
    seen.add(node.id);
    const hasChildren = node.children.length > 0;
    const expanded = hasChildren && isExpanded(node.id);
    const open = depth === 0 ? expanded : groupOpen;
    rows.push({ node, depth, hasChildren, expanded, root, groupOpen: open });
    if (expanded) node.children.forEach((c) => walk(c, depth + 1, root, open));
  };
  roots.forEach((r) => walk(r, 0, r, false));
  return rows;
}
